import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PageHero from "@/components/PageHero";
import { Megaphone, Music, Presentation, MonitorPlay, Signpost, Speaker, CheckCircle2, ArrowRight } from "lucide-react";

const systems = [
  {
    icon: Megaphone,
    title: "Public Address (PA) Systems",
    desc: "Zone-based paging and announcement systems for offices, factories, schools and retail — clear speech, every corner covered.",
    points: ["Multi-zone paging with priority override", "Emergency / evacuation broadcast ready", "Microphone consoles & schedulers"],
    chips: ["100V LINE", "ZONING", "EVAC READY"],
  },
  {
    icon: Music,
    title: "Background Music (BGM)",
    desc: "Curated ambient audio for retail, F&B, hotels and lobbies — scheduled playlists with independent zone volume control.",
    points: ["Multi-source music players", "Independent zone level control", "Timer-based scheduling"],
    chips: ["MULTI-ZONE", "SCHEDULING", "STREAMING"],
  },
  {
    icon: Presentation,
    title: "Conference Room AV",
    desc: "Boardrooms and meeting rooms with displays, video conferencing, wireless presentation and ceiling audio — meetings that just work.",
    points: ["Video conferencing (Teams / Zoom ready)", "Wireless presentation & switching", "Ceiling / table microphones & DSP"],
    chips: ["4K DISPLAY", "TEAMS / ZOOM", "DSP AUDIO"],
  },
  {
    icon: MonitorPlay,
    title: "Video Walls",
    desc: "Seamless LED and LCD video walls for control rooms, showrooms and lobbies — with processors for any layout you need.",
    points: ["Narrow-bezel LCD & fine-pitch LED", "Video wall processors & matrices", "Control room visualisation"],
    chips: ["FINE PITCH", "24/7 RATED", "MATRIX"],
  },
  {
    icon: Signpost,
    title: "Digital Signage",
    desc: "Dynamic menu boards, directories and promotional displays managed centrally — update every screen from one dashboard.",
    points: ["Cloud CMS content management", "Menu boards & wayfinding", "Scheduling & dayparting"],
    chips: ["CLOUD CMS", "4K", "CENTRAL MGMT"],
  },
  {
    icon: Speaker,
    title: "Acoustic Sound Systems",
    desc: "Professionally tuned sound reinforcement for ballrooms, houses of worship, gyms and function halls.",
    points: ["Line array & point-source speakers", "Digital mixers & amplifiers", "Acoustic tuning & commissioning"],
    chips: ["LINE ARRAY", "DSP TUNED", "PRO AUDIO"],
  },
];

export default function PaAvSystems() {
  return (
    <div data-testid="pa-av-systems-page">
      <PageHero
        label="PA & AV Systems"
        title="Public Address & Audio-Visual Solutions"
        subtitle="From building-wide paging to boardroom AV and video walls — communication and presentation systems designed for clarity."
        image="https://images.unsplash.com/photo-1680992046626-418f7e910589?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHw0fHxzbWFydCUyMGJ1aWxkaW5nJTIwZWx2JTIwc3lzdGVtJTIwc2VydmVyJTIwcm9vbSUyMG5ldHdvcmslMjBjYWJsaW5nfGVufDB8fHx8MTc4ODM0MTM0MHww&ixlib=rb-4.1.0&q=85"
      />

      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {systems.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.1 }}
                className="glow-card p-6 flex flex-col"
                data-testid={`pa-av-card-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <div className="w-11 h-11 bg-blue-600/20 border border-cyan-400/30 flex items-center justify-center mb-4">
                  <s.icon className="w-6 h-6 text-cyan-400" />
                </div>
                <h3 className="font-display text-lg font-semibold text-white">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.desc}</p>
                <ul className="mt-4 space-y-2">
                  {s.points.map((p) => (
                    <li key={p} className="flex gap-2 text-sm text-slate-300">
                      <CheckCircle2 className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" /> {p}
                    </li>
                  ))}
                </ul>
                <div className="mt-4 flex flex-wrap gap-2 pt-2">
                  {s.chips.map((c) => <span key={c} className="spec-chip">{c}</span>)}
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-14 text-center">
            <Link
              to="/quote"
              data-testid="pa-av-quote-cta"
              className="inline-flex items-center gap-2 px-8 py-4 bg-blue-600 hover:bg-blue-500 text-white font-semibold transition-colors border border-cyan-400/40"
            >
              Get a PA / AV Proposal <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
